<script setup>
// NOTICE: KAPTION-INTEGRATION
// Language selection modal component for translation feature

import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
} from 'dashboard/constants/kaptionLanguages';
import Modal from 'dashboard/components/Modal.vue';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['update:show', 'close']);

const store = useStore();
const { canUseFeature } = useFeaturePermissions();
const languageSearchQuery = ref('');

// Get current chat from store
const currentChat = computed(() => store.getters.getSelectedChat);

// Get conversation language from additional_attributes
const conversationLanguage = computed(() => {
  return (
    currentChat.value?.additional_attributes?.conversation_language ||
    currentChat.value?.additionalAttributes?.conversationLanguage ||
    DEFAULT_LANGUAGE
  );
});

// Get current language name
const currentLanguageName = computed(() => {
  const lang = AVAILABLE_LANGUAGES.find(
    l => l.code === conversationLanguage.value
  );
  return lang
    ? lang.name
    : AVAILABLE_LANGUAGES.find(l => l.code === DEFAULT_LANGUAGE)?.name ||
        'Español';
});

// Filter languages based on search query
const filteredLanguages = computed(() => {
  if (!languageSearchQuery.value.trim()) {
    return AVAILABLE_LANGUAGES;
  }
  const query = languageSearchQuery.value.toLowerCase();
  return AVAILABLE_LANGUAGES.filter(
    lang =>
      lang.name.toLowerCase().includes(query) ||
      lang.code.toLowerCase().includes(query)
  );
});

// Close modal
const closeModal = () => {
  emit('update:show', false);
  emit('close');
  languageSearchQuery.value = '';
};

// Select a new target language
const selectLanguage = async languageCode => {
  if (!canUseFeature('conversation_language_selection')) return;
  // Emit event to notify other components (like ReplyBox)
  emitter.emit('kaption-conversation-language-changed', {
    conversationId: props.conversationId,
    language: languageCode,
  });

  closeModal();
};

// Watch for show changes to reset search
const showModel = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <Modal
    v-if="canUseFeature('conversation_language_selection')"
    v-model:show="showModel"
    :on-close="closeModal"
    modal-type="centered"
  >
    <div class="language-modal-content">
      <div class="language-modal-header">
        <h3 class="language-modal-title">
          Select Language
          <span class="language-modal-subtitle">
            (Current: {{ currentLanguageName }})
          </span>
        </h3>
      </div>

      <!-- Search Input -->
      <div class="language-modal-search">
        <input
          v-model="languageSearchQuery"
          type="text"
          class="language-search-input"
          :placeholder="$t('CONVERSATION.LIVE_TRANSLATION.SEARCH_LANGUAGES')"
          autofocus
        />
      </div>

      <!-- Language List -->
      <div class="language-modal-list">
        <div
          v-for="lang in filteredLanguages"
          :key="lang.code"
          class="language-modal-item"
          :class="{
            'language-modal-item-active': lang.code === conversationLanguage,
          }"
          @click="selectLanguage(lang.code)"
        >
          <KaptionCircleFlag
            :language-code="lang.code"
            size="size-6"
            class="language-modal-flag"
          />
          <span class="language-modal-name">{{ lang.name }}</span>
          <span class="language-modal-code">{{ lang.code }}</span>
        </div>
        <div v-if="filteredLanguages.length === 0" class="language-modal-empty">
          {{ $t('CONVERSATION.LIVE_TRANSLATION.NO_LANGUAGES_FOUND') }}
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
/* Language Modal Styles */
/* Override modal container width for language modal - scoped to this component */
:deep(.modal-mask .modal-container) {
  max-width: 360px !important;
  width: auto !important;
  min-width: auto !important;
}

.language-modal-content {
  padding: 1.5rem;
  width: 100%;
}

.language-modal-header {
  margin-bottom: 1rem;
}

.language-modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  @apply dark:text-n-slate-12;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.language-modal-subtitle {
  font-size: 0.875rem;
  font-weight: 400;
  color: #6b7280;
  @apply dark:text-n-slate-10;
}

.language-modal-search {
  margin-bottom: 1rem;
}

.language-search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  @apply dark:bg-n-slate-2 dark:border-n-slate-6 dark:text-n-slate-12;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    @apply dark:border-n-blue-9;
  }

  &::placeholder {
    color: #9ca3af;
    @apply dark:text-n-slate-10;
  }
}

.language-modal-list {
  max-height: 300px;
  overflow-y: auto;
  border-radius: 0.5rem;
}

.language-modal-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  margin-bottom: 0.25rem;

  &:hover {
    background-color: #f3f4f6;
    @apply dark:bg-n-slate-3;
  }
}

.language-modal-item-active {
  background-color: #d1fae5;
  border: 1px solid rgba(34, 197, 94, 0.5);
  @apply dark:bg-n-green-3 dark:border-n-green-9;
}

.language-modal-flag {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.language-modal-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  flex: 1;
  @apply dark:text-n-slate-12;
}

.language-modal-code {
  font-size: 0.75rem;
  color: #9ca3af;
  text-transform: uppercase;
  font-weight: 500;
  @apply dark:text-n-slate-10;
}

.language-modal-empty {
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  @apply dark:text-n-slate-10;
}
</style>
